/**
 * Take a custom web component that acts like a form input field and wrap it so that it
 * will support "required" and "disabled" attributes like a native browser form element.
 * This approach does not use HTMLElement.attachInternals() since it isn't supported by
 * Firefox/Safari. With polyfills this could be an alternate solution however.
 *
 * Requirements:
 * - The component must have a property that stores its state (default "value")
 * - The component must emit either 'change' or 'input' events (or both)
 * - The static "properties" property should return all necessary properties/attributes
 *   for the component so that they can be proxied appropriately.
 *
 * Usage:
 *
 *   import { formComponent } from './form-component.js';
 *   class MyComponent extends ... {
 *     ...
 *     static get properties() {
 *       ...
 *     }
 *   }
 *   OmniElement.register('my-component-impl', MyComponent);
 *   OmniElement.register('my-component', formComponent(MyComponent));
 *
 *
 * Now in your html you can create your component normally with these two additional
 * optional attributes:
 *
 *   <my-component required disabled ... />
 *
 * @param {Object} Cls - The web component class
 * @param {Object} options
 * @param {string} options.valueProperty - The property that stores Cls's state
 * @param {string} options.styles - Extra CSS styles to apply to the root element
 * @return {Object} The returned class wraps Cls adding a hidden input field which
 * enables "required" functionality. Also handles proxying attributes/properties to the
 * the underlying field. When disabled, a white overlay will be placed over the
 * component and the cursor will be "not-allowed" on hover.
 */
export function formComponent(
  Cls,
  { valueProperty = 'value', styles = {} } = {}
) {
  return class extends HTMLElement {
    constructor() {
      super();

      let clsProps = {};
      let targetCls = Cls;
      while (targetCls) {
        clsProps = { ...targetCls.properties, ...clsProps };
        targetCls = Object.getPrototypeOf(targetCls); // traverse up inheritance chain
      }

      if (!clsProps?.[valueProperty]) {
        throw new Error(
          `Wrapped form element must have a '${valueProperty}' property`
        );
      }

      this.impl = new Cls();
      this._properties = {
        ...clsProps,
        ...Object.fromEntries(
          Object.keys(clsProps).map(name => [name.toLowerCase(), true])
        ),
      };

      // Create proxy setters for all properties listed in Cls.properties
      Object.keys(this._properties).forEach(propName => {
        Object.defineProperty(this, propName, {
          get: () => this.impl[propName],
          set: val => {
            this.impl[propName] = val;
            this.updateDOM();
          },
        });
      });

      // Edge-case when we need to create an additional "value" property
      if (valueProperty !== 'value') {
        Object.defineProperty(this, 'value', {
          get: () => this.impl[valueProperty],
          set: val => {
            this.impl[valueProperty] = val;
            this.updateDOM();
          },
        });
      }
    }

    static get observedAttributes() {
      // For all impl properties, observe corresponding attribute (+ required & disabled)
      return Object.keys({
        ...this._properties,
        required: true,
        disabled: true,
      });
    }

    attributeChangedCallback(name, oldVal, newVal) {
      // Proxy observed attribute changes to underlying impl & re-render
      if (this._properties[name]) this.impl.setAttribute(name, newVal);
      this.updateDOM();
    }

    connectedCallback() {
      this.createDOM();

      // Set all current attributes on to impl (if in properties)
      this.getAttributeNames().forEach(name => {
        if (this._properties[name])
          this.impl.setAttribute(name, this.getAttribute(name));
        else if (name === '_class')
          this.impl.setAttribute('class', this.getAttribute(name));
      });

      // Keep the hidden text input field in sync with impl field and redispatch event
      // on change & input events.
      ['input', 'change'].forEach(eventName => {
        this.impl.addEventListener(eventName, e => {
          e.stopPropagation();
          const div = this.getElementsByTagName('div')[0];
          if (!div) return;
          const input = div.getElementsByTagName('input')[0];
          input.value = this.impl[valueProperty] ?? '';
          this.dispatchEvent(new e.constructor(e.type, e));
          this.updateDOM();
        });
      });
    }

    createDOM() {
      // Create the base element structure of this component
      // <div>
      //   <input />
      //   <impl />
      //   <div />
      // </div>

      // The hidden input element which will let us use "required"
      const input = document.createElement('input');
      input.addEventListener('focus', e => e.target.blur());
      input.value = this.impl[valueProperty] ?? '';
      if (this.getAttribute('name') !== null)
        input.setAttribute('name', this.getAttribute('name'));

      // The overlay div used when "disabled" to cover the element
      const overlay = document.createElement('div');

      // The main container div
      const div = document.createElement('div');
      div.appendChild(input);
      div.appendChild(this.impl);
      div.appendChild(overlay);

      this.appendChild(div);

      // Remove any borders from root element
      this.style.setProperty('border-width', '0px', 'important');

      // Apply any specified host styles
      Object.entries(styles).forEach(([name, value]) =>
        this.style.setProperty(name, value)
      );

      // Set any additional styles/properties
      this.updateDOM();
    }

    updateDOM() {
      // If initialized (div exists) update all styles/attributes
      const div = this.getElementsByTagName('div')[0];
      if (!div) return;

      const input = div.getElementsByTagName('input')[0];
      const overlay = div.getElementsByTagName('div')[0];
      const disabled = this.hasAttribute('disabled');
      const required = this.hasAttribute('required');

      // The container div
      div.style.display = 'grid';
      div.style.position = 'relative';
      div.style['grid-template-columns'] = 'minmax(0,1fr)';

      // Main impl component
      this.impl.style['grid-area'] = '1/1';
      this.impl.style['pointer-events'] = disabled ? 'none' : 'initial';
      this.impl[disabled ? 'setAttribute' : 'removeAttribute']('tabindex', -1);

      // Hidden Input
      input.setAttribute('type', 'text');
      input.setAttribute('tabindex', -1);
      input.setAttribute('size', 1); // Override default size of 20 to avoid "min-width" behavior
      input.style.height = '100%';
      input.style.width = '100%';
      input.style.opacity = 0;
      input.style.padding = 0;
      input.style.margin = 0;
      input.style.border = 0;
      input.style['z-index'] = -9999;
      input.style['grid-area'] = '1/1';
      input[required ? 'setAttribute' : 'removeAttribute']('required', '');
      input[disabled ? 'setAttribute' : 'removeAttribute']('disabled', '');

      // Overlay when disabled
      overlay.setAttribute('class', 'is-overlay has-background-white');
      overlay.classList[disabled ? 'remove' : 'add']('is-hidden');
      overlay.style.opacity = 0.4;
      overlay.style.cursor = 'not-allowed';
      overlay.style['z-index'] = 1;
    }
  };
}
